import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import Notification from '../components/Notification';
import moderationService from '../services/moderationService';
import formatUrl from '../utils/urlFormatter';
import '../styles/BlockedUsers.css';
import { UserContext } from '../contexts/UserContext';

const BlockedUsers = () => {
    const navigate = useNavigate();
    const { redirectToLogin } = useContext(UserContext);
    const [blockedUsers, setBlockedUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [notification, setNotification] = useState({ message: '', type: '', header: '' });

    const fetchBlockedUsers = async () => {
        setLoading(true);
        try {
            const data = await moderationService.getBlockedUsers();
            // Ensure data is always an array
            setBlockedUsers(Array.isArray(data) ? data : []);
        } catch (error) {
            setNotification({
                message: error.message || 'Erreur lors du chargement des utilisateurs bloqués',
                type: 'error',
                header: 'Erreur',
            });
            setBlockedUsers([]); // Set empty array on error
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const token = Cookies.get('authToken');
        if (!token) {
            redirectToLogin(window.location.pathname);
            return;
        }
        fetchBlockedUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleUnblock = async (blockId, userName) => {
        if (!window.confirm(`Êtes-vous sûr de vouloir débloquer ${userName} ?`)) {
            return;
        }

        try {
            await moderationService.unblockUser(blockId);
            setNotification({
                message: `${userName} a été débloqué avec succès`,
                type: 'success',
                header: 'Déblocage réussi',
            });
            // Remove from list - ensure blockedUsers is array
            setBlockedUsers(prev => Array.isArray(prev) ? prev.filter(block => block.id !== blockId) : []);
        } catch (error) {
            setNotification({
                message: error.message || 'Erreur lors du déblocage',
                type: 'error',
                header: 'Erreur',
            });
        }
    };

    const filteredUsers = Array.isArray(blockedUsers) ? blockedUsers.filter(block => {
        const blockedUser = block.blocked_user_details;
        if (!blockedUser) return false;
        
        const searchLower = searchQuery.toLowerCase();
        const fullName = `${blockedUser.first_name || ''} ${blockedUser.last_name || ''}`.toLowerCase();
        
        return fullName.includes(searchLower);
    }) : [];

    const getUserInitials = (firstName, lastName) => {
        const first = (firstName || 'U')[0];
        const last = (lastName || 'U')[0];
        return `${first}${last}`.toUpperCase();
    };

    const getRandomColor = (seed) => {
        const colors = ['#003366', '#c51618', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6'];
        return colors[seed % colors.length];
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    return (
        <div className="blocked-users-container">
            {notification.message && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    header={notification.header}
                    onClose={() => setNotification({ message: '', type: '', header: '' })}
                />
            )}

            <div className="blocked-users-search">
                <div className="search-input-wrapper">
                    <i className="fas fa-search search-icon"></i>
                    <input
                        type="text"
                        placeholder="Rechercher un utilisateur bloqué..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="search-input"
                    />
                    {searchQuery && (
                        <button
                            className="clear-search"
                            onClick={() => setSearchQuery('')}
                        >
                            <i className="fas fa-times"></i>
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="blocked-users-list">
                    {[1, 2, 3, 4, 5].map((index) => (
                        <div key={index} className="blocked-user-card skeleton-card">
                            <div className="user-info">
                                <div className="user-avatar-skeleton skeleton"></div>
                                <div className="user-details">
                                    <div className="skeleton skeleton-name"></div>
                                    <div className="skeleton skeleton-username"></div>
                                    <div className="skeleton skeleton-date"></div>
                                </div>
                            </div>
                            <div className="user-actions">
                                <div className="skeleton skeleton-button"></div>
                                <div className="skeleton skeleton-button-large"></div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : filteredUsers.length === 0 ? (
                <div className="empty-state">
                    {searchQuery ? (
                        <>
                            <i className="fas fa-search empty-icon"></i>
                            <h3>Aucun résultat</h3>
                            <p>Aucun utilisateur bloqué ne correspond à votre recherche</p>
                        </>
                    ) : (
                        <>
                            <i className="fas fa-user-slash empty-icon"></i>
                            <h3>Aucun utilisateur bloqué</h3>
                            <p>Vous n'avez bloqué aucun utilisateur pour le moment</p>
                        </>
                    )}
                </div>
            ) : (
                <div className="blocked-users-list">
                    <div className="list-header">
                        <span className="result-count">
                            {filteredUsers.length} utilisateur{filteredUsers.length > 1 ? 's' : ''} bloqué{filteredUsers.length > 1 ? 's' : ''}
                        </span>
                    </div>
                    
                    {filteredUsers.map((block) => {
                        const user = block.blocked_user_details;
                        return (
                            <div key={block.id} className="blocked-user-card">
                                <div className="user-info">
                                    <div 
                                        className="user-avatar"
                                        style={{
                                            backgroundColor: (user.profile_picture || user.profile_picture_url) ? 'transparent' : getRandomColor(user.id || block.id)
                                        }}
                                    >
                                        {(user.profile_picture || user.profile_picture_url) ? (
                                            <img
                                                src={formatUrl(user.profile_picture || user.profile_picture_url)}
                                                alt={user.username}
                                                loading="lazy"
                                            />
                                        ) : (
                                            <span className="initials">
                                                {getUserInitials(user.first_name, user.last_name)}
                                            </span>
                                        )}
                                    </div>
                                    <div className="user-details">
                                        <h3 className="user-name">
                                            {`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Utilisateur'}
                                        </h3>
                                        <p className="block-date">
                                            <i className="fas fa-ban"></i>
                                            Bloqué le {formatDate(block.created_at)}
                                        </p>
                                        {block.reason && (
                                            <p className="block-reason">
                                                <i className="fas fa-info-circle"></i>
                                                {block.reason}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="user-actions">
                                    <button
                                        className="view-profile-btn"
                                        onClick={() => navigate(`/user/corner/${user.uuid || user.id}`)}
                                        title="Voir le profil"
                                    >
                                        <i className="fas fa-user"></i>
                                    </button>
                                    <button
                                        className="unblock-btn"
                                        onClick={() => handleUnblock(block.id, user.username)}
                                        title="Débloquer"
                                    >
                                        <i className="fas fa-unlock"></i>
                                        Débloquer
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default BlockedUsers;
